import { useCallback, useEffect, useState } from "react";
import type { SceneNodeInfo } from "../../../../shared/api-contract";
import { useViewer } from "../../../../state/ViewerProvider";
import { useDomainState } from "../../../../state/editor/useEditor";
import { useCaps } from "../../CapsProvider";
import { Icon } from "../../../kit";

/**
 * Scene hierarchy tree (v14). Reads getSceneTree(); each node shows an
 * expand chevron, a visibility eye (setNodeVisible), and — for mesh nodes —
 * click-to-select via its material group. Refetches on model/tree changes.
 * The kind icon mirrors iJewel (mesh/group/light/camera).
 */
const KIND_ICON: Record<SceneNodeInfo["kind"], string> = {
  group: "folder",
  mesh: "deployed_code",
  light: "light_mode",
  camera: "videocam",
};

export function HierarchyTree() {
  const { client } = useViewer();
  const r = client.ring;
  const caps = useCaps();
  const selection = useDomainState("selection");
  const models = useDomainState("models");
  const [tree, setTree] = useState<SceneNodeInfo[]>([]);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [hovered, setHovered] = useState<string | null>(null);

  const refresh = useCallback(() => {
    r.getSceneTree().then(setTree).catch(() => {});
  }, [r]);

  useEffect(() => {
    refresh();
    const off1 = client.on("modelsChanged", refresh);
    const off2 = client.on("sceneTreeChanged", refresh);
    // Viewport -> tree hover sync (webgi highlights the hovered node in the
    // hierarchy panel).
    const off3 = client.on("hoverChanged", (e) => setHovered(e.groupId));
    return () => { off1(); off2(); off3(); };
  }, [client, refresh, models.length]);

  // Tree -> viewport hover sync (v31 `setHoveredGroup`) — mirrors the
  // viewport-driven highlight the other direction so hovering a row in the
  // hierarchy panel highlights the corresponding part on canvas.
  const hoverGroup = useCallback((id: string | null) => {
    if (caps.v31) r.setHoveredGroup(id);
  }, [r, caps.v31]);

  if (tree.length === 0) {
    return <span className="block py-1 text-[12px] text-ed-text-3">Load a model to see its hierarchy.</span>;
  }

  const toggle = (id: string) =>
    setExpanded((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });

  const Row = ({ node, depth }: { node: SceneNodeInfo; depth: number }) => {
    const hasChildren = node.children.length > 0;
    const open = expanded.has(node.id);
    const selected = !!node.groupId && node.groupId === selection;
    const isHovered = !!node.groupId && node.groupId === hovered;
    return (
      <>
        <div
          className={`flex items-center gap-1 rounded py-0.5 pr-1 ${selected ? "bg-ed-active" : isHovered ? "bg-ed-hover" : "hover:bg-ed-hover"}`}
          style={{ paddingLeft: 4 + depth * 12 }}
          onMouseEnter={() => node.groupId && hoverGroup(node.groupId)}
          onMouseLeave={() => node.groupId && hoverGroup(null)}
        >
          <button
            onClick={() => hasChildren && toggle(node.id)}
            className={`flex h-4 w-4 items-center justify-center ${hasChildren ? "text-ed-text-3" : "opacity-0"}`}
          >
            <Icon name={open ? "expand_more" : "chevron_right"} size={14} />
          </button>
          <Icon name={KIND_ICON[node.kind]} size={13} className="shrink-0 text-ed-text-3" />
          <button
            onClick={() => node.groupId && r.selectGroup(node.groupId)}
            className="min-w-0 flex-1 truncate text-left text-[12px] text-ed-text"
            title={node.name}
          >
            {node.name}
            {node.kind !== "mesh" && node.meshCount > 0 ? (
              <span className="ml-1 text-[11px] text-ed-text-3">({node.meshCount})</span>
            ) : null}
          </button>
          <button
            title={node.visible ? "Hide" : "Show"}
            onClick={() => r.setNodeVisible(node.id, !node.visible).then(refresh)}
            className="flex h-5 w-5 items-center justify-center text-ed-text-3 hover:text-ed-text"
          >
            <Icon name={node.visible ? "visibility" : "visibility_off"} size={14} />
          </button>
        </div>
        {open && hasChildren ? node.children.map((c) => <Row key={c.id} node={c} depth={depth + 1} />) : null}
      </>
    );
  };

  return (
    <div className="scroll-thin flex max-h-[40vh] flex-col overflow-auto">
      {tree.map((n) => (
        <Row key={n.id} node={n} depth={0} />
      ))}
    </div>
  );
}
